// CB:BEGIN pm-projects-page
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import ContentLimiter from '../components/layout/ContentLimiter';
import { ClipboardList, Plus } from 'lucide-react';

const BRAND_GREEN = '#123c3b';

interface Property { id: string; label?: string; address1: string; city: string; state: string; }
interface Project {
  id: string; address: string; city?: string; state?: string;
  projectType: string; status: string; priority: string;
  requestedResponseBy?: string; createdAt: string;
  property?: { id: string; label?: string; address1: string; city: string; state: string };
  bids?: { id: string }[];
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: '#fef3c7', color: '#92400e' }, POSTED: { bg: '#dbeafe', color: '#1d4ed8' },
  BIDS_COLLECTING: { bg: '#fef9c3', color: '#854d0e' }, BIDS_READY: { bg: '#dcfce7', color: '#166534' },
  DECIDING: { bg: '#fef9c3', color: '#854d0e' }, IN_PROGRESS: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P1: { bg: '#e0f2fe', color: '#0369a1' }, ACTIVE_P2: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P3: { bg: '#e0f2fe', color: '#0369a1' }, COMPLETED: { bg: '#dcfce7', color: '#166534' },
  CANCELLED: { bg: '#fee2e2', color: '#dc2626' },
};

const PRIORITY_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  EMERGENCY: { bg: '#fee2e2', color: '#dc2626', label: 'Emergency' },
  HIGH: { bg: '#fef3c7', color: '#b45309', label: 'High' },
  NORMAL: { bg: '#f1f5f9', color: '#475569', label: 'Normal' },
};

function fmt(d?: string) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
}

function statusLabel(s: string) {
  const m: Record<string, string> = { DRAFT: 'Draft', POSTED: 'Posted', BIDS_COLLECTING: 'Collecting Bids', BIDS_READY: 'Bids Ready', DECIDING: 'Deciding', IN_PROGRESS: 'In Progress', ACTIVE_P1: 'Active (P1)', ACTIVE_P2: 'Active (P2)', ACTIVE_P3: 'Active (P3)', COMPLETED: 'Completed', CANCELLED: 'Cancelled' };
  return m[s] || s;
}

interface Props { onNavigate: (screen: string) => void; }

export default function PropertyManagerProjectsPage({ onNavigate }: Props) {
  const { getToken } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const freshToken = await getToken().catch(() => null);
      if (!freshToken) { setLoading(false); return; }
      const headers = { Authorization: `Bearer ${freshToken}` };
      const [projRes, propRes] = await Promise.all([
        fetch(getApiUrl('/api/property-manager/projects'), { headers, credentials: 'include' }),
        fetch(getApiUrl('/api/property-manager/properties'), { headers, credentials: 'include' }),
      ]);
      if (projRes.ok) { const d = await projRes.json(); setProjects(d.projects || []); }
      if (propRes.ok) { const d = await propRes.json(); setProperties(d.properties || []); }
    } finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = projects.filter(p => {
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterPropertyId && p.property?.id !== filterPropertyId) return false;
    if (filterPriority && p.priority !== filterPriority) return false;
    return true;
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-projects" />

      <ContentLimiter>
        <div style={{ padding: '32px 0' }}>
          {/* Page header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>Projects</h1>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{filtered.length} of {projects.length} projects</div>
            </div>
            <button
              onClick={() => onNavigate('pm-dashboard')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              <Plus size={14} /> New Project
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#374151' }}>
              <option value="">All Statuses</option>
              {['DRAFT', 'POSTED', 'BIDS_COLLECTING', 'BIDS_READY', 'DECIDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
            <select value={filterPropertyId} onChange={e => setFilterPropertyId(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#374151' }}>
              <option value="">All Properties</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.label || p.address1}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#374151' }}>
              <option value="">All Priorities</option>
              <option value="EMERGENCY">Emergency</option>
              <option value="HIGH">High</option>
              <option value="NORMAL">Normal</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '60px 32px', textAlign: 'center' }}>
              <ClipboardList size={40} style={{ color: '#cbd5e1', marginBottom: 12 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>No projects found</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                {filterStatus || filterPropertyId || filterPriority ? 'Try adjusting your filters.' : 'Create your first project from the dashboard.'}
              </div>
              {!filterStatus && !filterPropertyId && !filterPriority && (
                <button onClick={() => onNavigate('pm-dashboard')} style={{ padding: '10px 24px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Go to Dashboard</button>
              )}
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {['Property', 'Work Type', 'Priority', 'Status', 'Bids', 'Response By', 'Created'].map(col => (
                        <th key={col} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => {
                      const sc = STATUS_COLORS[p.status] || { bg: '#f1f5f9', color: '#475569' };
                      const pc = PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.NORMAL;
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                          onClick={() => onNavigate(`/pm/projects/${p.id}`)}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}
                        >
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{p.property?.label || p.property?.address1 || p.address}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{p.property ? `${p.property.city}, ${p.property.state}` : `${p.city || ''}, ${p.state || ''}`}</div>
                          </td>
                          <td style={{ padding: '14px 16px', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>{p.projectType.charAt(0) + p.projectType.slice(1).toLowerCase()}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ display: 'inline-block', background: pc.bg, color: pc.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{pc.label}</span>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ display: 'inline-block', background: sc.bg, color: sc.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{statusLabel(p.status)}</span>
                          </td>
                          <td style={{ padding: '14px 16px', fontSize: 13, color: '#374151', textAlign: 'center' }}>{p.bids?.length ?? 0}</td>
                          <td style={{ padding: '14px 16px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{fmt(p.requestedResponseBy)}</td>
                          <td style={{ padding: '14px 16px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmt(p.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </ContentLimiter>
    </div>
  );
}
// CB:END pm-projects-page
